import AppCard from '@nxsys/components/AppCard';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppRateForm, { AppRateFormItem } from '@nxsys/components/AppRateForm';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import { countryList } from '@nxsys/utils';
import type { FormProps } from 'antd';
import { Col, Form, Row, Typography } from 'antd';
import React, {
  Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';

const { Text } = Typography;

export type AssignmentFormValues = {
  assignmentName?: string;
  projectRef?: string;
  clientRef?: string;
  status?: Option<string> | null;
  siteId?: Option<string> | null;
  employeeAndSubcontractor?: any;
  agencyName?: any;
  startDate?: string;
  endDate?: string;
  invoicingAddress?: {
    address1?: string;
    address2?: string;
    address3?: string;
    city?: string;
    county?: string;
    postCode?: string;
    country?: Option<string> | null;
  };
  workLocation?: AssignmentFormValues['invoicingAddress'];
  rates?: Array<Partial<AppRateFormItem>>;
  attachment?: File | string | null;
};

export type AssignmentFormRef = {
  submit: () => void;
};

type AssignmentFormProps = {
  defaultValues?: Partial<AssignmentFormValues>;
  formRef?: Ref<AssignmentFormRef>;
  onSubmit?: (values: AssignmentFormValues) => void;
  submitting?: boolean;
};

const innerDefaultValues = {
  assignmentName: '',
  projectRef: '',
  clientRef: '',
  status: { label: 'ACTIVE', value: 'ACTIVE' },
  siteId: null,
  employeeAndSubcontractor: null,
  agencyName: null,
  startDate: '',
  endDate: '',
  invoicingAddress: {
    address1: '',
    address2: '',
    address3: '',
    city: '',
    county: '',
    postCode: '',
    country: null,
  },
  workLocation: {
    address1: '',
    address2: '',
    address3: '',
    city: '',
    county: '',
    postCode: '',
    country: null,
  },
  rates: [
    {
      rateCode: null,
      rateType: '',
      description: '',
      rate: '',
      vatDetails: null,
      vatRate: '',
    },
  ],
  attachment: null,
};

const AssignmentForm: React.FC<AssignmentFormProps> = ({
  defaultValues = innerDefaultValues,
  onSubmit,
  formRef,
}: AssignmentFormProps) => {
  const { payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const { messages } = useTypedIntl();
  const [form] = Form.useForm<AssignmentFormValues>();
  const [sameAsInvoicing, setSameAsInvoicing] = useState(true);
  const watchInvoicingAddress = Form.useWatch('invoicingAddress', form);
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const statusOptions = useMemo(
    () => [
      { label: messages['common.active'], value: 'ACTIVE' },
      { label: messages['common.inactive'], value: 'INACTIVE' },
    ],
    [messages],
  );

  const onFinish: FormProps<AssignmentFormValues>['onFinish'] = (values) => {
    const mergedValues: AssignmentFormValues = {
      ...defaultValues,
      ...values,
      workLocation: sameAsInvoicing
        ? values.invoicingAddress
        : values.workLocation,
    };
    onSubmit?.(mergedValues);
  };

  const onFinishFailed: FormProps<AssignmentFormValues>['onFinishFailed'] = (
    errorInfo,
  ) => {
    console.log('Failed:', errorInfo);
  };

  useEffect(() => {
    if (sameAsInvoicing) {
      form.setFieldsValue({
        workLocation: watchInvoicingAddress,
      });
    }
  }, [watchInvoicingAddress, sameAsInvoicing]);

  const handleSelectAgency = (value: any) => {
    const agencyData = value?.fullData as IAgency;
    form.setFieldsValue({
      agencyName: value,
      invoicingAddress: {
        address1: agencyData?.address?.[0] || '',
        address2: agencyData?.address?.[1] || '',
        address3: agencyData?.address?.[2] || '',
        city: agencyData?.city || '',
        county: agencyData?.county || '',
        postCode: agencyData?.postCode || '',
        country: agencyData?.country
          ? countryList.find((c) => c.value === agencyData.country)
          : undefined,
      },
    });
  };

  return (
    <div>
      <AppCard title={messages['assignment.section.detail']}>
        <AppFormContainer
          form={form}
          initialValues={{
            ...innerDefaultValues,
            ...defaultValues,
          }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          className="px-4 py-2"
        >
          <Row gutter={[48, 48]}>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['assignment.form.assignmentName']}
                name="assignmentName"
                rules={[
                  {
                    required: true,
                    message: messages['form.pleaseInput'],
                  },
                ]}
              >
                <AppInput
                  placeholder={messages['assignment.form.enterAssignmentName']}
                />
              </Form.Item>
              {!isIreland && (
                <Form.Item
                  label={messages['assignment.form.agencyName']}
                  name="agencyName"
                  rules={[{ required: true }]}
                >
                  <AppSelectSync
                    placeholder={messages['assignment.form.selectAgency']}
                    apiType="searchAgency"
                    type="agency"
                    onChangeValue={handleSelectAgency}
                  />
                </Form.Item>
              )}
              <Form.Item
                label={messages['common.employee']}
                name="employeeAndSubcontractor"
                rules={[{ required: true }]}
              >
                <AppSelectSync
                  placeholder={messages['form.selectEmployeeAndSubcontractor']}
                  apiType="searchEmployeeSubcontractV2"
                  type="emp&sub"
                  onChangeValue={(value: any) => {
                    form.setFieldsValue({ employeeAndSubcontractor: value });
                  }}
                />
              </Form.Item>
              <Form.Item
                label={messages['assignment.form.clientRef']}
                name="clientRef"
              >
                <AppInput
                  placeholder={messages['assignment.form.enterClientRef']}
                />
              </Form.Item>
              {!isIreland && (
                <Form.Item
                  label={messages['assignment.form.siteId']}
                  name="siteId"
                >
                  <AppSelectSync
                    placeholder={messages['assignment.form.selectSiteId']}
                    apiType="searchSiteInAgency"
                    type="agencySite"
                    onChangeValue={(value: any) => {
                      form.setFieldsValue({ siteId: value });
                    }}
                    searchId={watchValues?.agencyName?.value}
                  />
                </Form.Item>
              )}
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['assignment.form.status']}
                name="status"
                rules={[{ required: true }]}
              >
                <AppSelect
                  options={statusOptions}
                  placeholder={messages['assignment.form.selectStatus']}
                />
              </Form.Item>
              <Form.Item
                label={messages['assignment.form.projectRef']}
                name="projectRef"
              >
                <AppInput
                  placeholder={messages['assignment.form.enterProjectRef']}
                />
              </Form.Item>
              <Form.Item
                label={messages['assignment.form.startDate']}
                name="startDate"
                rules={[{ required: true }]}
              >
                <AppDatePicker
                  placeholder={messages['assignment.form.selectDate']}
                  format={'DD/MM/YYYY'}
                />
              </Form.Item>
              <Form.Item
                label={messages['assignment.form.endDate']}
                name="endDate"
              >
                <AppDatePicker
                  placeholder={messages['assignment.form.selectDate']}
                  format={'DD/MM/YYYY'}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.attachment']}
                name="attachment"
              >
                <AppUploadFile
                  urlFile={
                    typeof watchValues?.attachment === 'string'
                      ? watchValues?.attachment
                      : undefined
                  }
                  onChange={(file: File) => {
                    form.setFieldsValue({ attachment: file });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </AppFormContainer>
      </AppCard>

      {!isIreland && (
        <Row gutter={[16, 16]} className="mt-4">
          <Col xs={24} md={12}>
            <AppCard title={messages['assignment.detail.invoicingAddress']}>
              <AppFormContainer
                form={form}
                initialValues={defaultValues}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                labelCol={{ span: 4 }}
                wrapperCol={{ span: 20 }}
                className="px-4 py-2"
              >
                <Row gutter={[16, 12]}>
                  <Col span={24}>
                    <Form.Item
                      label={messages['assignment.address.address1']}
                      name={['invoicingAddress', 'address1']}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterAddress']
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label={messages['assignment.address.address2']}
                      name={['invoicingAddress', 'address2']}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterAddress']
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label={messages['assignment.address.address3']}
                      name={['invoicingAddress', 'address3']}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterAddress']
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={14}>
                    <Form.Item
                      label={messages['assignment.address.city']}
                      name={['invoicingAddress', 'city']}
                      labelCol={{ span: 7 }}
                    >
                      <AppInput
                        placeholder={messages['assignment.address.enterCity']}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={10}>
                    <Form.Item
                      name={['invoicingAddress', 'county']}
                      wrapperCol={{ span: 24 }}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.selectCounty']
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={14}>
                    <Form.Item
                      label={messages['assignment.address.postCode']}
                      name={['invoicingAddress', 'postCode']}
                      labelCol={{ span: 7 }}
                      rules={[
                        postCodeValidationRule(messages, payrollCountry),
                      ]}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterPostCode']
                        }
                        upperCaseAll
                        onChange={(e) => {
                          form.setFieldValue(
                            ['invoicingAddress', 'postCode'],
                            formatPostCode(e.target.value, payrollCountry),
                          );
                        }}
                        maxLength={8}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={10}>
                    <Form.Item
                      name={['invoicingAddress', 'country']}
                      wrapperCol={{ span: 24 }}
                    >
                      <AppSelect
                        placeholder={
                          messages['assignment.address.selectCountry']
                        }
                        options={countryList}
                        className="w-full !min-w-0"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </AppFormContainer>
            </AppCard>
          </Col>

          <Col xs={24} md={12}>
            <AppCard
              title={
                <div className="flex items-center justify-between gap-3">
                  <span>{messages['assignment.detail.workLocation']}</span>
                  <div className="flex items-center gap-2">
                    <AppCheckbox
                      checked={sameAsInvoicing}
                      onChange={(e) => setSameAsInvoicing(e.target.checked)}
                    />
                    <Text type="secondary" className="ms-2">
                      {messages['assignment.address.sameAsInvoicing']}
                    </Text>
                  </div>
                </div>
              }
            >
              <AppFormContainer
                form={form}
                initialValues={defaultValues}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                labelCol={{ span: 4 }}
                wrapperCol={{ span: 20 }}
                className="px-4 py-2"
              >
                <Row gutter={[16, 12]}>
                  <Col span={24}>
                    <Form.Item
                      label={messages['assignment.address.address1']}
                      name={['workLocation', 'address1']}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterAddress']
                        }
                        disabled={sameAsInvoicing}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label={messages['assignment.address.address2']}
                      name={['workLocation', 'address2']}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterAddress']
                        }
                        disabled={sameAsInvoicing}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label={messages['assignment.address.address3']}
                      name={['workLocation', 'address3']}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterAddress']
                        }
                        disabled={sameAsInvoicing}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={14}>
                    <Form.Item
                      label={messages['assignment.address.city']}
                      name={['workLocation', 'city']}
                      labelCol={{ span: 7 }}
                    >
                      <AppInput
                        placeholder={messages['assignment.address.enterCity']}
                        disabled={sameAsInvoicing}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={10}>
                    <Form.Item
                      name={['workLocation', 'county']}
                      wrapperCol={{ span: 24 }}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.selectCounty']
                        }
                        disabled={sameAsInvoicing}
                        className="w-full !min-w-0"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={14}>
                    <Form.Item
                      label={messages['assignment.address.postCode']}
                      name={['workLocation', 'postCode']}
                      labelCol={{ span: 7 }}
                      rules={[
                        postCodeValidationRule(messages, payrollCountry),
                      ]}
                    >
                      <AppInput
                        placeholder={
                          messages['assignment.address.enterPostCode']
                        }
                        disabled={sameAsInvoicing}
                        upperCaseAll
                        onChange={(e) => {
                          form.setFieldValue(
                            ['workLocation', 'postCode'],
                            formatPostCode(e.target.value, payrollCountry),
                          );
                        }}
                        maxLength={8}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24} md={10}>
                    <Form.Item
                      name={['workLocation', 'country']}
                      wrapperCol={{ span: 24 }}
                    >
                      <AppSelect
                        placeholder={
                          messages['assignment.address.selectCountry']
                        }
                        options={countryList}
                        disabled={sameAsInvoicing}
                        className="w-full !min-w-0"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </AppFormContainer>
            </AppCard>
          </Col>
        </Row>
      )}

      <AppRateForm
        className="my-4"
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        defaultValues={defaultValues}
        listHidden={['units', 'net', 'total']}
      />
    </div>
  );
};

export default AssignmentForm;
